import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { movimientosService, cuentasService } from '../services/api';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const FORM_PAGO = { fecha: '', monto: '', medio_pago: 'transferencia', cuenta_bancaria_id: '', referencia: '', notas: '' };

const fmtDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const calcPeriodo = (id) => {
  const hoy = new Date();
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  switch (id) {
    case 'este_mes':
      return { desde: fmtDateISO(new Date(y, m, 1)), hasta: fmtDateISO(new Date(y, m + 1, 0)) };
    case 'mes_anterior':
      return { desde: fmtDateISO(new Date(y, m - 1, 1)), hasta: fmtDateISO(new Date(y, m, 0)) };
    case 'este_trimestre': {
      const q = Math.floor(m / 3);
      return { desde: fmtDateISO(new Date(y, q * 3, 1)), hasta: fmtDateISO(new Date(y, q * 3 + 3, 0)) };
    }
    case 'este_anio':
      return { desde: `${y}-01-01`, hasta: fmtDateISO(hoy) };
    case 'desde_inicio':
      return { desde: '2020-01-01', hasta: fmtDateISO(hoy) };
    default:
      return null;
  }
};

export default function Pagos() {
  const navigate = useNavigate();
  const inicial = calcPeriodo('este_mes');
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [periodoActivo, setPeriodoActivo] = useState('este_mes');
  const [contratos, setContratos] = useState([]);
  const [stats, setStats] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [vistaLista, setVistaLista] = useState(false);
  const [modal, setModal] = useState(false);
  const [contratoSel, setContratoSel] = useState(null);
  const [formPago, setFormPago] = useState(FORM_PAGO);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    if (!desde || !hasta) return;
    setCargando(true);
    try {
      const hoy = new Date();
      await movimientosService.generarCargos({ anio: hoy.getFullYear(), mes: hoy.getMonth() + 1, retroactivo: true });
      const res = await movimientosService.getResumen(desde, hasta);
      setContratos(res.data.contratos || []);
      setStats(res.data.stats || []);
    } catch (err) {
      console.error('Error cargando pagos:', err);
    }
    setCargando(false);
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { cuentasService.getAll().then(r => setCuentas(r.data)); }, []);

  const seleccionarPeriodo = (id) => {
    setPeriodoActivo(id);
    if (id !== 'personalizado') {
      const p = calcPeriodo(id);
      if (p) { setDesde(p.desde); setHasta(p.hasta); }
    }
  };

  const navegarMes = (delta) => {
    const d = new Date(desde + 'T00:00:00');
    d.setMonth(d.getMonth() + delta);
    const y = d.getFullYear();
    const m = d.getMonth();
    setDesde(fmtDateISO(new Date(y, m, 1)));
    setHasta(fmtDateISO(new Date(y, m + 1, 0)));
    // Check if matches a known preset
    const hoy = new Date();
    if (y === hoy.getFullYear() && m === hoy.getMonth()) {
      setPeriodoActivo('este_mes');
    } else if ((y === hoy.getFullYear() && m === hoy.getMonth() - 1) || (hoy.getMonth() === 0 && y === hoy.getFullYear() - 1 && m === 11)) {
      setPeriodoActivo('mes_anterior');
    } else {
      setPeriodoActivo('personalizado');
    }
  };

  const getPeriodoDisplay = () => {
    const dDesde = new Date(desde + 'T00:00:00');
    const dHasta = new Date(hasta + 'T00:00:00');
    if (dDesde.getMonth() === dHasta.getMonth() && dDesde.getFullYear() === dHasta.getFullYear()) {
      return `${MESES[dDesde.getMonth()]} ${dDesde.getFullYear()}`;
    }
    if (dDesde.getFullYear() === dHasta.getFullYear()) {
      return `${MESES[dDesde.getMonth()]} — ${MESES[dHasta.getMonth()]} ${dHasta.getFullYear()}`;
    }
    return `${MESES[dDesde.getMonth()]} ${dDesde.getFullYear()} — ${MESES[dHasta.getMonth()]} ${dHasta.getFullYear()}`;
  };

  const handleDesdeChange = (e) => { setDesde(e.target.value); setPeriodoActivo('personalizado'); };
  const handleHastaChange = (e) => { setHasta(e.target.value); setPeriodoActivo('personalizado'); };

  const fmtMonto = (monto, moneda) => {
    const n = parseFloat(monto || 0);
    return `${moneda === 'USD' ? 'US$' : '$'} ${n.toLocaleString('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Stats por moneda
  const getStatMoneda = (moneda) => {
    const s = stats.find(x => x.moneda === moneda);
    return s ? { cargo: parseFloat(s.total_cargo), pagado: parseFloat(s.total_pagado) } : { cargo: 0, pagado: 0 };
  };
  const monedas = [...new Set(stats.map(s => s.moneda))];

  // Estado de cada contrato
  const getEstado = (c) => {
    const saldo = parseFloat(c.saldo_total);
    if (saldo <= 0) return 'al_dia';
    const hoy = new Date();
    if (hoy.getDate() > 10) return 'vencido';
    return 'pendiente';
  };

  // Filtros
  const contratosFiltrados = contratos.filter(c => {
    if (filtroEstado === 'todos') return true;
    return getEstado(c) === filtroEstado;
  });

  const cantAlDia = contratos.filter(c => getEstado(c) === 'al_dia').length;
  const cantVencido = contratos.filter(c => getEstado(c) === 'vencido').length;
  const cantPendiente = contratos.filter(c => getEstado(c) === 'pendiente').length;

  // Modal cobrar
  const abrirCobrar = (c) => {
    setContratoSel(c);
    const saldo = parseFloat(c.saldo_total);
    setFormPago({
      ...FORM_PAGO,
      fecha: new Date().toISOString().split('T')[0],
      monto: saldo > 0 ? saldo : parseFloat(c.monto_contrato),
    });
    setModal(true);
  };

  const handlePagar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    await movimientosService.pagar({ contrato_id: contratoSel.contrato_id, ...formPago });
    setModal(false);
    setContratoSel(null);
    setFormPago(FORM_PAGO);
    setGuardando(false);
    cargar();
  };

  const cuentasFiltradas = cuentas.filter(c => {
    if (!contratoSel) return true;
    return c.moneda === contratoSel.moneda;
  });

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1>Pagos</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div className="view-toggle">
            <button className={!vistaLista ? 'active' : ''} onClick={() => setVistaLista(false)}>Tarjetas</button>
            <button className={vistaLista ? 'active' : ''} onClick={() => setVistaLista(true)}>Lista</button>
          </div>
        </div>
      </div>

      {/* Navegación de período */}
      <div className="mes-nav">
        <button className="btn btn-secondary" onClick={() => navegarMes(-1)}>&larr;</button>
        <h2 className="mes-titulo">{getPeriodoDisplay()}</h2>
        <button className="btn btn-secondary" onClick={() => navegarMes(1)}>&rarr;</button>
        <select value={periodoActivo} onChange={e => seleccionarPeriodo(e.target.value)}
          style={{ marginLeft: 12, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.88rem' }}>
          <option value="este_mes">Este mes</option>
          <option value="mes_anterior">Mes anterior</option>
          <option value="este_trimestre">Este trimestre</option>
          <option value="este_anio">Ano actual</option>
          <option value="desde_inicio">Desde inicio</option>
          <option value="personalizado">Personalizado</option>
        </select>
        {periodoActivo === 'personalizado' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 12 }}>
            <label style={{ fontSize: '0.85rem', color: '#666' }}>Desde</label>
            <input type="date" value={desde} onChange={handleDesdeChange} className="mes-input" />
            <label style={{ fontSize: '0.85rem', color: '#666' }}>Hasta</label>
            <input type="date" value={hasta} onChange={handleHastaChange} className="mes-input" />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: `repeat(${monedas.length * 3}, 1fr)` }}>
        {monedas.map(moneda => {
          const s = getStatMoneda(moneda);
          const pendiente = s.cargo - s.pagado;
          return (
            <React.Fragment key={moneda}>
              <div className="stat-card">
                <div className="label">A cobrar {moneda}</div>
                <div className="number">{fmtMonto(s.cargo, moneda)}</div>
              </div>
              <div className="stat-card" style={{ borderLeft: '4px solid #28a745' }}>
                <div className="label">Cobrado {moneda}</div>
                <div className="number" style={{ color: '#28a745' }}>{fmtMonto(s.pagado, moneda)}</div>
              </div>
              <div className="stat-card" style={{ borderLeft: `4px solid ${pendiente > 0 ? '#ffc107' : '#28a745'}` }}>
                <div className="label">Pendiente {moneda}</div>
                <div className="number" style={{ color: pendiente > 0 ? '#856404' : '#28a745' }}>{fmtMonto(pendiente, moneda)}</div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Filtros */}
      <div className="filtros-bar">
        <div className="filtro-pills">
          <button className={`pill ${filtroEstado === 'todos' ? 'active' : ''}`} onClick={() => setFiltroEstado('todos')}>
            Todos
          </button>
          <button className={`pill ${filtroEstado === 'vencido' ? 'active' : ''}`} onClick={() => setFiltroEstado('vencido')}>
            Vencido <span className="pill-count">{cantVencido}</span>
          </button>
          <button className={`pill ${filtroEstado === 'pendiente' ? 'active' : ''}`} onClick={() => setFiltroEstado('pendiente')}>
            Pendiente <span className="pill-count">{cantPendiente}</span>
          </button>
          <button className={`pill ${filtroEstado === 'al_dia' ? 'active' : ''}`} onClick={() => setFiltroEstado('al_dia')}>
            Al dia <span className="pill-count">{cantAlDia}</span>
          </button>
        </div>
      </div>

      {cargando ? (
        <div className="empty-state"><p>Cargando...</p></div>
      ) : contratosFiltrados.length === 0 ? (
        <div className="empty-state"><p>No hay contratos activos para este periodo</p></div>
      ) : vistaLista ? (
        /* Vista tabla */
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Inmueble</th>
                <th>Inquilino</th>
                <th style={{ textAlign: 'right' }}>Cargos</th>
                <th style={{ textAlign: 'right' }}>Pagado</th>
                <th style={{ textAlign: 'right' }}>Saldo total</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {contratosFiltrados.map(c => {
                const estado = getEstado(c);
                const saldo = parseFloat(c.saldo_total);
                return (
                  <tr key={c.contrato_id}>
                    <td><strong>{c.inmueble_nombre}</strong></td>
                    <td>{c.inquilino_nombre} {c.inquilino_apellido || ''}</td>
                    <td style={{ textAlign: 'right' }}>{fmtMonto(c.cargo_periodo, c.moneda)}</td>
                    <td style={{ textAlign: 'right', color: '#28a745' }}>{parseFloat(c.pagado_periodo) > 0 ? fmtMonto(c.pagado_periodo, c.moneda) : '-'}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: saldo > 0 ? '#e74c3c' : '#28a745' }}>
                      {saldo > 0 ? fmtMonto(saldo, c.moneda) : 'Al dia'}
                    </td>
                    <td><span className={`badge ${estado}`}>{estado === 'al_dia' ? 'Al dia' : estado === 'vencido' ? 'Vencido' : 'Pendiente'}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '0.78rem' }}
                          onClick={() => navigate(`/cuenta-corriente/${c.contrato_id}`)}>Ver cuenta</button>
                        {saldo > 0 && (
                          <button className="btn btn-primary" style={{ padding: '6px 10px', fontSize: '0.78rem' }}
                            onClick={() => abrirCobrar(c)}>Cobrar</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        /* Vista tarjetas */
        <div className="cards-grid">
          {contratosFiltrados.map(c => {
            const estado = getEstado(c);
            const saldo = parseFloat(c.saldo_total);
            return (
              <div key={c.contrato_id} className={`card ${estado}`}>
                <div className="card-header">
                  <h3>{c.inmueble_nombre}</h3>
                  <span className={`badge ${estado}`}>{estado === 'al_dia' ? 'Al dia' : estado === 'vencido' ? 'Vencido' : 'Pendiente'}</span>
                </div>
                <p>{c.inquilino_nombre} {c.inquilino_apellido || ''}</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', marginTop: 8, fontSize: '0.88rem' }}>
                  <p style={{ color: '#888' }}>Cargos: <strong style={{ color: '#333' }}>{fmtMonto(c.cargo_periodo, c.moneda)}</strong></p>
                  <p style={{ color: '#888' }}>Pagado: <strong style={{ color: '#28a745' }}>{parseFloat(c.pagado_periodo) > 0 ? fmtMonto(c.pagado_periodo, c.moneda) : '-'}</strong></p>
                </div>
                <p className="monto" style={{ color: saldo > 0 ? '#e74c3c' : '#28a745', marginTop: 8 }}>
                  {saldo > 0 ? `Saldo: ${fmtMonto(saldo, c.moneda)}` : 'Al dia'}
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }}
                    onClick={() => navigate(`/cuenta-corriente/${c.contrato_id}`)}>Ver cuenta</button>
                  {saldo > 0 && (
                    <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => abrirCobrar(c)}>Cobrar</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal cobrar rapido */}
      {modal && contratoSel && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Registrar pago</h2>
            <p style={{ color: '#666', marginBottom: 16, fontSize: '0.9rem' }}>
              <strong>{contratoSel.inmueble_nombre}</strong> &middot; {contratoSel.inquilino_nombre} {contratoSel.inquilino_apellido || ''}
              <br />Saldo adeudado: <strong style={{ color: '#e74c3c' }}>{fmtMonto(contratoSel.saldo_total, contratoSel.moneda)}</strong>
              <br /><span style={{ fontSize: '0.8rem', color: '#999' }}>El pago se asigna automaticamente al alquiler mas antiguo adeudado</span>
            </p>
            <form onSubmit={handlePagar}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fecha de pago *</label>
                  <input type="date" value={formPago.fecha} onChange={e => setFormPago(f => ({ ...f, fecha: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Monto *</label>
                  <input type="number" step="0.01" value={formPago.monto} onChange={e => setFormPago(f => ({ ...f, monto: e.target.value }))} required />
                </div>
                <div className="form-group">
                  <label>Medio de pago</label>
                  <select value={formPago.medio_pago} onChange={e => setFormPago(f => ({ ...f, medio_pago: e.target.value }))}>
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                {formPago.medio_pago === 'transferencia' && (
                  <div className="form-group">
                    <label>Cuenta bancaria</label>
                    <select value={formPago.cuenta_bancaria_id} onChange={e => setFormPago(f => ({ ...f, cuenta_bancaria_id: e.target.value }))}>
                      <option value="">Seleccionar...</option>
                      {cuentasFiltradas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label>Referencia</label>
                  <input value={formPago.referencia} onChange={e => setFormPago(f => ({ ...f, referencia: e.target.value }))} placeholder="Nro. transferencia, cheque, etc." />
                </div>
                <div className="form-group full">
                  <label>Notas</label>
                  <textarea value={formPago.notas} onChange={e => setFormPago(f => ({ ...f, notas: e.target.value }))} rows={2} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Registrar pago'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
